import React, { useEffect, useMemo, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { Flame, X, ChevronLeft, ChevronRight, Search, Package, Lock, FileText, Download, Eye, Clock, Printer } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Aliquota IVA italiana applicata ai prezzi mobili/arredi.
 */
const VAT_RATE = 0.22;

function stripVat(amount) {
  return Math.ceil(Number(amount || 0) / (1 + VAT_RATE));
}

function ceilEuro(amount) {
  return Math.ceil(Number(amount || 0));
}

/**
 * Politica prezzi:
/* Pricing rules (lug 2026):
 *  - Trader: vede price_trader GIÀ SCORPORATO IVA nel DB (auto: offerta/1.22,
 *    arrotondato all'euro superiore) — disclaimer "IVA esclusa".
 *  - Studio: vede price_offer LORDO (IVA + trasporto + montaggio inclusi) —
 *    disclaimer "incl. IVA, trasporto e montaggio entro 50 km". Il prezzo
 *    proposto è quello che il cliente paga effettivamente, no nasce sorprese.
 *  - Real Estate: come Studio, vede price_offer LORDO (consumer-grade).
 *  - Admin: vede Listino + Scontato Studio (price_offer). Il NETTO (price_net)
 *    NON è mai mostrato in Outlet: resta solo nella gestione prodotti (tab admin).
 *  - Listino e Offerta nei DB sono salvati IVA inclusa (al pubblico).
 */
function buildPrice(p, item, user) {
  const flashPct = item.discount_pct || 0;
  const role = user?.role;
  const type = user?.account_type;
  const listinoRaw = Number(p.price_retail || 0);
  // Solo i Trader vedono il netto (B2B); Studio e Real Estate vedono il LORDO
  // come il consumatore finale, perché il loro "prezzo proposto" deve già
  // includere IVA + trasporto + montaggio.
  const stripVatForRole = (role !== "admin") && (type === "trader");
  const listino = stripVatForRole ? stripVat(listinoRaw) : ceilEuro(listinoRaw);
  if (flashPct > 0 && role === "admin") {
    const base = p.price_offer != null ? Number(p.price_offer) : listinoRaw;
    const discounted = Math.ceil(base * (1 - flashPct / 100));
    return { main: discounted, original: ceilEuro(base), tagKey: "tag_offer_admin", discountPct: Math.round(flashPct), locked: false };
  }
  if (!user) {
    return { main: ceilEuro(listinoRaw), original: null, tagKey: "tag_listino", discountPct: 0, locked: true };
  }
  if (role === "admin") {
    if (p.price_offer != null) {
      const offerRaw0 = Number(p.price_offer);
      const pct = Math.round(p.offer_discount_pct || (listinoRaw > 0 ? (1 - offerRaw0 / listinoRaw) * 100 : 0));
      return { main: ceilEuro(offerRaw0), original: ceilEuro(listinoRaw), tagKey: "tag_offer_admin", discountPct: pct, locked: false };
    }
    return { main: ceilEuro(listinoRaw), original: null, tagKey: "tag_listino", discountPct: 0, locked: false };
  }
  if (type === "trader") {
    const traderRaw = p.price_trader;
    if (traderRaw != null) {
      // price_trader è salvato GIÀ scorporato IVA (auto: offerta / 1.22, ceil)
      const trader = ceilEuro(traderRaw);
      const pct = listino > 0 ? Math.round((1 - trader / listino) * 100) : 0;
      return { main: trader, original: listino, tagKey: "tag_trader", discountPct: pct, locked: false };
    }
    return { main: listino, original: null, tagKey: "tag_listino", discountPct: 0, locked: false };
  }
  const offerRaw = p.price_offer;
  if (offerRaw != null) {
    const offer = ceilEuro(offerRaw);
    const pct = Math.round(p.offer_discount_pct || (listinoRaw > 0 ? (1 - Number(offerRaw) / listinoRaw) * 100 : 0));
    return { main: offer, original: listino, tagKey: "tag_offer", discountPct: pct, locked: false };
  }
  return { main: listino, original: null, tagKey: "tag_listino", discountPct: 0, locked: false };
}

function getPriceDisclaimerKey(user) {
  const type = user?.account_type;
  // Studio + Real Estate vedono prezzo proposto già completo (IVA + trasporto + montaggio).
  if (type === "real_estate" || type === "studio") return "vat_included_full";
  return "vat_excluded";
}

function OutletCard({ item, onOpen, user }) {
  const { t } = useTranslation();
  const p = item.product;
  const price = buildPrice(p, item, user);
  const cover = (p.images && p.images.length > 0) ? `${process.env.REACT_APP_BACKEND_URL}/api/products/images/${p.images[0].id}?v=${p.images[0].size || 0}` : (p.image_url || "");
  const imgCount = (p.images && p.images.length) || (p.image_url ? 1 : 0);
  const isReserved = !!p.is_reserved;
  const reservedByMe = !!p.reserved_by_me;
  const reservedByOther = isReserved && !reservedByMe;
  const isAdmin = user?.role === "admin";
  // ADMIN: mostra in card SOLO Prezzo Listino + Scontato Studio (richiesta
  // utente giu 2026): sono i 2 prezzi più rilevanti per la negoziazione con
  // architetti. Trader e Netto restano visibili nel modale dettaglio.
  const adminPriceRows = isAdmin ? [
    { key: "tag_listino", val: ceilEuro(p.price_retail || 0), accent: false },
    { key: "tag_offer_admin", val: p.price_offer != null ? ceilEuro(p.price_offer) : null, accent: true },
  ].filter(r => r.val != null && r.val > 0) : [];
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className={`text-left bg-white border border-ink/10 rounded-sm overflow-hidden hover-lift focus:outline-none focus:ring-2 focus:ring-ink/20 ${reservedByOther ? "opacity-70" : ""}`}
      data-testid={item.kind === "sale" ? `flash-${item.id}` : `product-${item.id}`}
      data-reserved={isReserved ? (reservedByMe ? "by-me" : "by-other") : "no"}
    >
      <div className="relative overflow-hidden aspect-[4/3] bg-cream2">
        {cover ? <img src={cover} alt={p.name} className={`w-full h-full object-contain ${reservedByOther ? "grayscale" : ""}`} /> : <div className="w-full h-full bg-cream2 flex items-center justify-center text-slate1 text-xs">{t("outlet.no_image")}</div>}
        {price.discountPct > 0 && !price.locked && !reservedByOther && (
          <div className="absolute top-3 left-3 px-3 py-1.5 bg-gold text-cream text-[11px] tracking-[0.2em] uppercase">-{price.discountPct}%</div>
        )}
        {imgCount > 1 && (
          <div className="absolute top-3 right-3 px-2 py-1 bg-ink/70 text-cream text-[10px] tracking-[0.15em] uppercase rounded-sm">
            {t("outlet.photos_count", { count: imgCount })}
          </div>
        )}
        {reservedByOther && (
          <div className="absolute inset-0 flex items-center justify-center bg-ink/35 backdrop-blur-[1px]">
            <div className="px-4 py-2 bg-ink text-cream text-[11px] tracking-[0.25em] uppercase inline-flex items-center gap-2 rounded-sm shadow-lg">
              <Lock size={12} /> {t("outlet.tag_reserved_48h")}
            </div>
          </div>
        )}
        {reservedByMe && (
          <div className="absolute bottom-3 left-3 px-2.5 py-1 bg-emerald-700 text-cream text-[10px] tracking-[0.2em] uppercase rounded-sm inline-flex items-center gap-1.5">
            <Clock size={11} /> {t("outlet.tag_reserved_by_me")}
          </div>
        )}
      </div>
      <div className="p-6">
        <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">{p.brand}</div>
        <div className="mt-2 font-display text-lg text-ink">{p.name}</div>
        {isAdmin ? (
          <div className="mt-4 space-y-1" data-testid={`outlet-admin-prices-${item.id}`}>
            {adminPriceRows.map((r) => (
              <div key={r.key} className="flex items-baseline justify-between gap-2">
                <span className={`text-[10px] tracking-[0.15em] uppercase ${r.accent ? "text-gold" : "text-slate1"}`}>{t(`outlet.${r.key}`)}</span>
                <span className={`font-mono ${r.accent ? "font-display text-xl text-ink" : "text-sm text-ink/80"}`}>€ {r.val.toLocaleString("it-IT")}</span>
              </div>
            ))}
            <div className="text-[10px] tracking-[0.2em] uppercase text-success pt-1">{t("outlet.stock_label")}: {item.stock}</div>
            <div className="text-[10px] text-slate1 italic leading-tight">{t("outlet.admin_price_disclaimer")}</div>
          </div>
        ) : (
          <div className="mt-4">
            {price.original != null && (
              <div className="text-xs text-slate1 line-through">€ {price.original.toLocaleString("it-IT")}</div>
            )}
            <div className="flex items-baseline gap-2">
              <div className="font-display text-2xl text-ink">€ {price.main.toLocaleString("it-IT")}</div>
              <span className="text-[9px] tracking-[0.2em] uppercase text-slate1">{t(`outlet.${price.tagKey}`)}</span>
            </div>
            {price.locked && (
              <div className="mt-1 text-[10px] text-gold inline-flex items-center gap-1">
                <Lock size={10} /> {t("outlet.register_for_price")}
              </div>
            )}
            <div className="text-[10px] tracking-[0.2em] uppercase text-success mt-1">{t("outlet.stock_label")}: {item.stock}</div>
            <div className="mt-2 text-[10px] text-slate1 italic">{t(`outlet.${getPriceDisclaimerKey(user)}`)}</div>
          </div>
        )}
      </div>
    </button>
  );
}

// Restituisce hh:mm rimanenti a partire da una data ISO futura.
function formatRemaining(isoDate, expiredLabel) {
  if (!isoDate) return "—";
  const exp = new Date(isoDate);
  if (Number.isNaN(exp.getTime())) return "—";
  const ms = exp - new Date();
  if (ms <= 0) return expiredLabel || "—";
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h ${m}m`;
}

export default function Outlet() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  // Pre-filtro per brand (es. arrivo da una scheda fornitore via `/outlet?brand=GERVASONI`).
  // Si applica solo se il param è presente nell'URL; clearable rimuovendolo.
  const brandFilter = (searchParams.get("brand") || "").trim();

  useEffect(() => {
    api.get("/flash-sales").then(r => setSales(r.data)).catch(() => {});
    api.get("/products").then(r => setProducts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowRight") nextImg();
      if (e.key === "ArrowLeft") prevImg();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active, imgIdx]);

  const openItem = (item) => {
    setActive(item);
    setImgIdx(0);
    // Tracking: registra l'apertura del dettaglio prodotto per trader/studio.
    // L'endpoint backend filtra automaticamente gli admin e dedup-a entro 5 minuti.
    // Best-effort: errori silenziosi per non interrompere il flow utente.
    try {
      const role = user?.role;
      const type = user?.account_type;
      if (role && role !== "admin" && (type === "trader" || type === "studio")) {
        const p = item?.product || {};
        const price = buildPrice(p, item, user);
        api.post("/outlet/view", {
          product_id: p.id || "",
          product_name: p.name || "",
          product_brand: p.brand || "",
          product_category: p.category || "",
          price_seen: Number(price?.main || 0),
          price_label: price?.tagKey || "",
        }).catch(() => {});
      }
    } catch {
      // ignore
    }
  };

  const flashItems = useMemo(
    () => sales.map((s) => ({ id: s.id, key: `sale-${s.id}`, kind: "sale", product: s.product, discount_pct: s.discount_pct || 0, stock: s.stock })),
    [sales]
  );

  const productItems = useMemo(() => {
    const flashProductIds = new Set(sales.map((s) => s.product?.id).filter(Boolean));
    return products
      .filter((p) => !flashProductIds.has(p.id))
      .map((p) => ({ id: p.id, key: `prod-${p.id}`, kind: "product", product: p, discount_pct: 0, stock: p.stock || 0 }))
      // Prodotti con foto prima, quelli senza foto sempre in fondo (ordine stabile)
      .sort((a, b) => {
        const ha = ((a.product.images?.length || 0) > 0 || a.product.image_url) ? 0 : 1;
        const hb = ((b.product.images?.length || 0) > 0 || b.product.image_url) ? 0 : 1;
        return ha - hb;
      });
  }, [products, sales]);

  function matchesQuery(item) {
    const k = query.trim().toLowerCase();
    const p = item.product || {};
    if (brandFilter) {
      const b = (p.brand || "").trim().toLowerCase();
      if (b !== brandFilter.toLowerCase()) return false;
    }
    if (categoryFilter) {
      const cat = (p.category || "").trim();
      if (cat !== categoryFilter) return false;
    }
    if (!k) return true;
    return (
      (p.name || "").toLowerCase().includes(k) ||
      (p.brand || "").toLowerCase().includes(k) ||
      (p.category || "").toLowerCase().includes(k)
    );
  }

  // Categorie disponibili (basate su flash + stock, ordinate alfabeticamente).
  // Mostriamo i pill solo se ci sono almeno 2 categorie distinte.
  const availableCategories = useMemo(() => {
    const set = new Set();
    [...flashItems, ...productItems].forEach((it) => {
      const c = (it.product?.category || "").trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "it"));
  }, [flashItems, productItems]);

  const filteredFlash = flashItems.filter(matchesQuery);
  const filteredProducts = productItems.filter(matchesQuery);

  const galleryUrls = (item) => {
    if (!item) return [];
    const p = item.product;
    const imgs = p.images && p.images.length > 0
      ? p.images.map(i => `${process.env.REACT_APP_BACKEND_URL}/api/products/images/${i.id}?v=${i.size || 0}`)
      : [];
    if (imgs.length === 0 && p.image_url) imgs.push(p.image_url);
    return imgs;
  };

  const nextImg = () => {
    if (!active) return;
    const urls = galleryUrls(active);
    if (urls.length === 0) return;
    setImgIdx((i) => (i + 1) % urls.length);
  };
  const prevImg = () => {
    if (!active) return;
    const urls = galleryUrls(active);
    if (urls.length === 0) return;
    setImgIdx((i) => (i - 1 + urls.length) % urls.length);
  };

  const activeUrls = galleryUrls(active);
  const activePrice = active ? buildPrice(active.product, active, user) : null;
  const activeFiles = active?.product?.files || [];
  const activeReserved = !!active?.product?.is_reserved;
  const activeReservedByMe = !!active?.product?.reserved_by_me;
  const canReserve = !!user && user.role !== "admin" && !activeReserved && (active?.stock || 0) > 0;
  const [reserveLoading, setReserveLoading] = useState(false);

  async function reloadActive(pid) {
    try {
      const r = await api.get(`/products/${pid}`);
      setProducts((prev) => prev.map((p) => p.id === pid ? r.data : p));
      setActive((prev) => prev ? { ...prev, product: r.data, stock: r.data.stock ?? prev.stock } : prev);
    } catch { /* ignore */ }
  }

  async function reserveActive() {
    if (!active?.product?.id) return;
    setReserveLoading(true);
    try {
      await api.post(`/products/${active.product.id}/reserve`);
      toast.success(t("outlet.reservation_success"));
      await reloadActive(active.product.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("outlet.reservation_error"));
    } finally { setReserveLoading(false); }
  }

  async function releaseActive() {
    if (!active?.product?.id) return;
    setReserveLoading(true);
    try {
      await api.post(`/products/${active.product.id}/release`);
      toast.success(t("outlet.reservation_released"));
      await reloadActive(active.product.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("outlet.release_error"));
    } finally { setReserveLoading(false); }
  }

  async function downloadProductPDF() {
    if (!active?.product?.id) return;
    const pid = active.product.id;
    const tab = window.open("", "_blank");
    try {
      const resp = await api.get(`/products/${pid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      if (tab) {
        tab.location.href = url;
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${active.product.brand || "prodotto"}_${active.product.name || ""}.pdf`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (tab) tab.close();
      toast.error(e?.response?.data?.detail || t("outlet.pdf_error"));
    }
  }

  const remainingLabel = (iso) => formatRemaining(iso, t("outlet.expired_label"));

  return (
    <div className="pt-28 pb-20 bg-cream min-h-[100svh]" data-testid="outlet-page">
      {/* Hero */}
      <div className="bg-ink text-cream py-20 mb-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.unsplash.com/photo-1570422211361-803d3660004d?w=1600&q=80" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-gold">
            <Flame size={14} /> {t("outlet.hero_pre")}
          </div>
          <h1 className="mt-4 font-display text-5xl lg:text-6xl font-light">{t("outlet.title")}</h1>
          <p className="mt-4 max-w-xl text-cream/70 text-lg">
            {t("outlet.subtitle")}
          </p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Brand filter banner (arrivo da scheda fornitore) */}
        {brandFilter && (
          <div
            className="mb-4 flex items-center justify-between gap-3 bg-gold/10 border border-gold/40 rounded-sm px-4 py-3"
            data-testid="outlet-brand-filter-banner"
          >
            <div className="flex items-center gap-2 text-sm text-ink">
              <span className="text-[10px] tracking-[0.2em] uppercase text-gold">{t("outlet.brand_filter_label")}</span>
              <span className="font-display text-base">{brandFilter}</span>
            </div>
            <button
              type="button"
              onClick={() => setSearchParams({})}
              className="text-[10px] tracking-[0.2em] uppercase text-slate1 hover:text-ink transition-colors flex items-center gap-1"
              data-testid="outlet-brand-filter-clear"
              aria-label={t("outlet.brand_filter_clear")}
            >
              <X size={12} /> {t("outlet.brand_filter_clear")}
            </button>
          </div>
        )}

        {/* Search */}
        <div className="mb-6 max-w-xl" data-testid="outlet-search">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate1" size={16} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("outlet.search_placeholder")}
              className="w-full pl-10 pr-4 py-3 border border-ink/20 bg-white rounded-sm text-sm focus:outline-none focus:border-ink"
              data-testid="outlet-search-input"
            />
          </div>
        </div>

        {/* Category filter pills */}
        {availableCategories.length >= 2 && (
          <div
            className="mb-10 -mx-1 flex flex-wrap gap-2"
            role="group"
            aria-label={t("outlet.category_filter_aria")}
            data-testid="outlet-category-filter"
          >
            <button
              type="button"
              onClick={() => setCategoryFilter("")}
              className={`px-4 py-1.5 text-[11px] tracking-[0.2em] uppercase rounded-sm border transition-colors ${
                categoryFilter === ""
                  ? "bg-ink text-cream border-ink"
                  : "bg-white text-slate1 border-ink/15 hover:border-ink hover:text-ink"
              }`}
              data-testid="outlet-category-all"
            >
              {t("outlet.category_all")}
            </button>
            {availableCategories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setCategoryFilter(cat === categoryFilter ? "" : cat)}
                className={`px-4 py-1.5 text-[11px] tracking-[0.2em] uppercase rounded-sm border transition-colors ${
                  categoryFilter === cat
                    ? "bg-ink text-cream border-ink"
                    : "bg-white text-slate1 border-ink/15 hover:border-ink hover:text-ink"
                }`}
                data-testid={`outlet-category-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Flash sales section */}
        {filteredFlash.length > 0 && (
          <section className="mb-16" data-testid="outlet-flash-section">
            <div className="flex items-center gap-2 mb-5">
              <Flame size={16} className="text-gold" />
              <h2 className="font-display text-2xl text-ink">{t("outlet.flash_section")}</h2>
              <span className="text-sm text-slate1">({filteredFlash.length})</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="outlet-grid">
              {filteredFlash.map((item) => <OutletCard key={item.key} item={item} onOpen={openItem} user={user} />)}
            </div>
          </section>
        )}

        {/* All products section */}
        <section data-testid="outlet-stock-section">
          <div className="flex items-center gap-2 mb-5">
            <Package size={16} className="text-ink" />
            <h2 className="font-display text-2xl text-ink">{t("outlet.stock_section")}</h2>
            <span className="text-sm text-slate1">({filteredProducts.length})</span>
          </div>
          {filteredProducts.length === 0 && filteredFlash.length === 0 ? (
            <div className="text-center text-slate1 py-16">
              {query ? t("outlet.none_match_search") : t("outlet.no_stock_now")}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-sm text-slate1 italic">{t("outlet.no_others")}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="outlet-stock-grid">
              {filteredProducts.map((item) => <OutletCard key={item.key} item={item} onOpen={openItem} user={user} />)}
            </div>
          )}
        </section>
      </div>

      {/* Product Modal */}
      {active && (
        <div
          className="fixed inset-0 z-[100] bg-ink/80 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
          onClick={() => setActive(null)}
          data-testid="outlet-modal"
        >
          <div
            className="bg-cream w-full max-w-5xl max-h-[90vh] overflow-auto rounded-sm relative grid grid-cols-1 md:grid-cols-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={downloadProductPDF}
              className="absolute top-3 right-14 z-10 h-9 px-3 flex items-center gap-1.5 bg-ink text-cream hover:opacity-90 border border-ink/10 rounded-sm text-[10px] tracking-[0.2em] uppercase"
              data-testid="outlet-modal-print-pdf"
              title={t("outlet.print_pdf_title")}
            >
              <Printer size={13} />
              {t("outlet.print_pdf")}
            </button>
            <button
              onClick={() => setActive(null)}
              className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center bg-white/90 hover:bg-white border border-ink/10 rounded-sm"
              data-testid="outlet-modal-close"
              aria-label={t("outlet.close_aria")}
            >
              <X size={18} />
            </button>

            {/* Gallery */}
            <div className="relative bg-cream2 aspect-square md:aspect-auto md:min-h-[480px]">
              {activeUrls.length > 0 ? (
                <img
                  src={activeUrls[imgIdx]}
                  alt={active.product.name}
                  className="w-full h-full object-contain"
                  data-testid="outlet-modal-image"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate1 text-xs">{t("outlet.no_image")}</div>
              )}

              {activeUrls.length > 1 && (
                <>
                  <button
                    onClick={prevImg}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white border border-ink/10 rounded-sm"
                    data-testid="outlet-modal-prev"
                    aria-label={t("outlet.prev_aria")}
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <button
                    onClick={nextImg}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white border border-ink/10 rounded-sm"
                    data-testid="outlet-modal-next"
                    aria-label={t("outlet.next_aria")}
                  >
                    <ChevronRight size={18} />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-ink/70 text-cream text-[11px] tracking-[0.15em] rounded-sm">
                    {imgIdx + 1} / {activeUrls.length}
                  </div>
                </>
              )}
            </div>

            {/* Info */}
            <div className="p-8 md:p-10 flex flex-col">
              <div className="text-[10px] tracking-[0.3em] uppercase text-slate1">{active.product.brand}</div>
              <h2 className="mt-2 font-display text-3xl text-ink">{active.product.name}</h2>
              <div className="mt-2 inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-gold">
                {activePrice?.discountPct > 0 ? (
                  <><Flame size={12} /> -{activePrice.discountPct}% · {t("outlet.stock_label")}: {active.stock}</>
                ) : (
                  <><Package size={12} /> {t("outlet.stock_label")}: {active.stock}</>
                )}
              </div>

              {active.product.description && (
                <p className="mt-5 text-sm text-slate1 leading-relaxed" data-testid="outlet-modal-description">
                  {i18n.language?.startsWith("en")
                    ? (active.product.description_en || (
                        <em className="text-slate1/70">{t("outlet.translation_unavailable")}</em>
                      ))
                    : active.product.description}
                </p>
              )}

              {active.product.category && (
                <div className="mt-4 text-[11px] tracking-[0.2em] uppercase text-slate1" data-testid="outlet-modal-category">
                  {t("outlet.category_lbl")}{" "}
                  <span className="text-ink">
                    {i18n.language?.startsWith("en")
                      ? (active.product.category_en || (
                          <em className="text-slate1/70 normal-case tracking-normal">{t("outlet.translation_unavailable")}</em>
                        ))
                      : active.product.category}
                  </span>
                </div>
              )}

              {activeFiles.length > 0 && (
                <div className="mt-5 pt-5 border-t border-ink/10" data-testid="outlet-modal-files">
                  <div className="text-[11px] tracking-[0.2em] uppercase text-slate1 mb-2 inline-flex items-center gap-1.5">
                    <FileText size={12} /> {t("outlet.files_section", { count: activeFiles.length })}
                  </div>
                  <ul className="space-y-1.5">
                    {activeFiles.map((f) => (
                      <li key={f.id} className="flex items-center gap-2 text-sm">
                        <FileText size={13} className="text-slate1 shrink-0" />
                        <span className="text-ink truncate flex-1">{f.title || f.filename}</span>
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/products/files/${f.id}/preview`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate1 hover:text-ink"
                          title={t("suppliers.asset_preview")}
                          data-testid={`product-file-preview-${f.id}`}
                        ><Eye size={14} /></a>
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/products/files/${f.id}/download`}
                          className="text-slate1 hover:text-ink"
                          title={t("suppliers.asset_download")}
                          data-testid={`product-file-download-${f.id}`}
                        ><Download size={14} /></a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-auto pt-8 border-t border-ink/10">
                {activePrice?.original != null && (
                  <div className="text-xs text-slate1 line-through">€ {activePrice.original.toLocaleString("it-IT")}</div>
                )}
                {activePrice?.locked ? (
                  <div className="bg-cream2 border border-gold/30 rounded-sm p-4">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-gold inline-flex items-center gap-1.5">
                      <Lock size={11} /> {t("outlet.reserved_price_pre")}
                    </div>
                    <div className="font-display text-2xl text-ink mt-1">{t("outlet.reserved_listino", { price: activePrice.main.toLocaleString("it-IT") })}</div>
                    <p className="mt-2 text-sm text-slate1">
                      <Trans i18nKey="outlet.reserved_price_body" />
                    </p>
                    <Link to="/register" className="mt-3 inline-block bg-ink text-cream px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm hover-lift">
                      {t("outlet.reserved_cta")}
                    </Link>
                  </div>
                ) : (
                  <>
                    <div className="flex items-baseline gap-3">
                      <div className="font-display text-4xl text-ink">€ {activePrice.main.toLocaleString("it-IT")}</div>
                      <span className="text-[10px] tracking-[0.2em] uppercase text-slate1">{t(`outlet.${activePrice.tagKey}`)}</span>
                    </div>
                    <div className="mt-2 text-[10px] text-slate1 italic">{t(`outlet.${getPriceDisclaimerKey(user)}`)}</div>

                    {/* Blocco prenotazione 48h */}
                    {user && user.role !== "admin" && (
                      <div className="mt-5" data-testid="outlet-reservation-block">
                        {activeReserved && !activeReservedByMe && (
                          <div className="p-4 bg-slate-100 border border-slate-300 rounded-sm" data-testid="outlet-reservation-locked">
                            <div className="text-[11px] tracking-[0.2em] uppercase text-slate-700 inline-flex items-center gap-1.5">
                              <Lock size={11} /> {t("outlet.reserved_locked_title")}
                            </div>
                            <div className="text-sm text-slate-700 mt-1">
                              <Trans i18nKey="outlet.reserved_locked_text" values={{ remaining: remainingLabel(active.product.reserved_until) }} />
                            </div>
                          </div>
                        )}
                        {activeReservedByMe && (
                          <div className="p-4 bg-emerald-50 border border-emerald-300 rounded-sm" data-testid="outlet-reservation-mine">
                            <div className="text-[11px] tracking-[0.2em] uppercase text-emerald-800 inline-flex items-center gap-1.5">
                              <Clock size={11} /> {t("outlet.reserved_mine_title")}
                            </div>
                            <div className="text-sm text-emerald-900 mt-1">
                              <Trans i18nKey="outlet.reserved_mine_text" values={{ remaining: remainingLabel(active.product.reserved_until) }} />
                            </div>
                            <button
                              onClick={releaseActive}
                              disabled={reserveLoading}
                              className="mt-3 inline-flex items-center gap-2 border border-emerald-700 text-emerald-800 px-4 py-2 text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-emerald-700 hover:text-cream transition-colors disabled:opacity-50"
                              data-testid="outlet-release-btn"
                            >
                              {t("outlet.release_btn")}
                            </button>
                          </div>
                        )}
                        {canReserve && (
                          <button
                            onClick={reserveActive}
                            disabled={reserveLoading}
                            className="w-full inline-flex items-center justify-center gap-2 bg-ink text-cream px-5 py-3 text-[11px] tracking-[0.2em] uppercase rounded-sm hover-lift disabled:opacity-50"
                            data-testid="outlet-reserve-btn"
                            title={t("outlet.reserve_btn_title")}
                          >
                            <Lock size={12} />
                            {reserveLoading ? t("outlet.reserving") : t("outlet.reserve_btn")}
                          </button>
                        )}
                        {canReserve && (
                          <div className="mt-2 text-[10px] text-slate1 italic">
                            {t("outlet.reservation_nonbinding")}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {activeUrls.length > 1 && (
                  <div className="mt-5 flex gap-2 overflow-x-auto">
                    {activeUrls.map((u, i) => (
                      <button
                        key={i}
                        onClick={() => setImgIdx(i)}
                        className={`flex-shrink-0 w-16 h-16 rounded-sm overflow-hidden border ${i === imgIdx ? "border-ink" : "border-ink/10 opacity-60 hover:opacity-100"}`}
                        data-testid={`outlet-modal-thumb-${i}`}
                      >
                        <img src={u} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
